<script lang="ts">
	import { Alert, AlertV2, Col, Input, Radio, Row, Stack, Text } from '@Shelly/shelly-design-system';
	import {
		TRANSFER_OWNERSHIP_ALERT_DEFAULT,
		type TransferActionMode,
		type TransferFormatFn,
	} from './transfer-ownership-helpers';
	import { transferProfilePasswordInputId } from './home-management-utils';
	import { getProfileSelectionLabel } from 'src/helpers/houses/homes_helpers';

	export let formatFn: TransferFormatFn;
	/** Design-system variant: V2 uses AlertV2; legacy uses Alert with type warning. */
	export let variant: 'v2' | 'legacy';
	export let updatedProfiles: { email: string; title: string }[];
	export let getProfileRadioId: (email: string) => string;
	export let manualEmailInputId: string;
	export let manualPasswordInputId: string;

	export let actionMode: TransferActionMode;
	export let selectedEmail: string;
	export let transferEmail: string;
	export let profileTransferPassword: string;
	export let manualTransferPassword: string;
	export let transferEmailError: string;
	export let transferPasswordError: string;
	export let showTransferFailureHelp: boolean;
	export let composedFailureMessageHtml: string;

	const selectProfile = (email: string): void => {
		actionMode = 'profile';
		selectedEmail = email;
		transferEmail = '';
		transferEmailError = '';
		profileTransferPassword = '';
		manualTransferPassword = '';
		transferPasswordError = '';
	};

	const activateManualMode = (): void => {
		actionMode = 'email';
		selectedEmail = '';
		profileTransferPassword = '';
		transferPasswordError = '';
	};
</script>

{#if variant === 'v2'}
	<AlertV2
		class="mb-m"
		heading={formatFn('settings_transfer_ownership_alert', {
			default: TRANSFER_OWNERSHIP_ALERT_DEFAULT,
		})}
	/>
{:else}
	<Alert
		class="mb-m"
		type="warning"
		heading={formatFn('settings_transfer_ownership_alert', {
			default: TRANSFER_OWNERSHIP_ALERT_DEFAULT,
		})}
	/>
{/if}

{#if updatedProfiles.length > 0}
	<Text styleType="body-small" class="mb-m">
		{formatFn('settings_transfer_recipient_info', {
			default:
				"Choose a saved account or enter the recipient's credentials to transfer the selected devices.",
		})}
	</Text>
	<Text element="h3" styleType="body-small" class="mb-s">
		{formatFn('text_modal_transfer_saved_account', {
			default: 'Transfer to a saved account',
		})}
	</Text>

	{#each updatedProfiles as profile}
		<div class="mb-s">
			<Row>
				<Col sm="10" md="11" lg="11">
					<Radio
						size="m"
						id={getProfileRadioId(profile.email)}
						value={profile.email}
						selected={actionMode === 'profile' ? selectedEmail : ''}
						on:click={() => selectProfile(profile.email)}
						>{getProfileSelectionLabel(profile.title, profile.email)}</Radio
					>
				</Col>
			</Row>
			{#if actionMode === 'profile' && selectedEmail === profile.email}
				<Input
					id={transferProfilePasswordInputId(profile.email, variant)}
					aria-labelledby={transferProfilePasswordInputId(profile.email, variant)}
					label={formatFn('label_password', {
						default: 'password',
					})}
					bind:value={profileTransferPassword}
					type="password"
					error={transferPasswordError}
					placeholder={formatFn('placeholder_password', {
						default: 'enter your password',
					})}
					a11yProps={{
						iconButtonAriaLabel: formatFn('button_show_password', {
							default: 'Show password',
						}),
					}}
				/>
			{/if}
		</div>
	{/each}

	<div class="separator mb-m mt-s" />
	<Text styleType="body-small" class="text-capitalize mb-m">
		{formatFn('or', {
			default: 'Or',
		})}
	</Text>
{/if}

<Text element="h3" styleType="body-small" class="mb-s">
	{formatFn('text_modal_transfer_with_new_account', {
		default: 'Transfer with another account',
	})}
</Text>

<Stack direction="column" gap="s">
	<Input
		id={manualEmailInputId}
		aria-labelledby={manualEmailInputId}
		label={formatFn('settings_sub_account_create_label_email', {
			default: 'Email',
		})}
		bind:value={transferEmail}
		type="email"
		error={transferEmailError}
		placeholder={formatFn('placeholder_email', {
			default: 'enter your e-mail',
		})}
		on:focus={activateManualMode}
		on:input={() => {
			transferEmailError = '';
			if (transferEmail) activateManualMode();
		}}
	/>
	<Input
		id={manualPasswordInputId}
		aria-labelledby={manualPasswordInputId}
		label={formatFn('label_password', {
			default: 'password',
		})}
		bind:value={manualTransferPassword}
		type="password"
		error={transferPasswordError}
		placeholder={formatFn('placeholder_password', {
			default: 'enter your password',
		})}
		a11yProps={{
			iconButtonAriaLabel: formatFn('button_show_password', {
				default: 'Show password',
			}),
		}}
	/>
</Stack>

{#if showTransferFailureHelp}
	{#if variant === 'v2'}
		<AlertV2
			isHeadingBold
			class="mt-m"
			heading={formatFn('transfer_ownership_failure_hint_heading', {
				default: 'Transfer could not be completed',
			})}
			message={composedFailureMessageHtml}
		/>
	{:else}
		<Alert
			class="mt-m"
			type="warning"
			isHeadingBold
			heading={formatFn('transfer_ownership_failure_hint_heading', {
				default: 'Transfer could not be completed',
			})}
			message={composedFailureMessageHtml}
		/>
	{/if}
{/if}

<style>
	.separator {
		border-top: 1px solid var(--color-border, #212128);
	}
</style>
